import { useEffect, useRef, useCallback, useState } from 'react'
// We no longer need these imports as the related code is commented out
// import { checkSessionValidity, getUserSessions } from '../utils/keycloakSessionService'
import { ExtendedAuth } from '../auth.types'
import axios from 'axios'

// Create storage keys
const SESSION_TRANSFER_FLAG_KEY = 'gcis_session_transfer_completed'
const LAST_ACTIVITY_KEY = 'gcis_last_activity_timestamp'
const SESSION_ACTIVE_KEY = 'gcis_session_active'

// Time constants
const ACTIVITY_CHECK_INTERVAL = 10000 // 10 seconds
const INACTIVITY_THRESHOLD = 5 * 60 * 1000 // 5 minutes of inactivity

/**
 * Hook to periodically validate if the current session is still active
 * If the user has been logged out from this session (e.g., by logging in from another browser),
 * this hook will automatically redirect them to the login page
 */
export const useSessionValidator = (auth: ExtendedAuth, intervalMs = 30000) => {
  const timerRef = useRef<number | undefined>(undefined)
  const activityTimerRef = useRef<number | undefined>(undefined)
  const [hasTransferredSession, setHasTransferredSession] = useState<boolean>(
    localStorage.getItem(SESSION_TRANSFER_FLAG_KEY) === 'true',
  )
  const [isActive, setIsActive] = useState<boolean>(true)

  // Function to mark that a session transfer has occurred
  const markSessionTransferred = useCallback(() => {
    localStorage.setItem(SESSION_TRANSFER_FLAG_KEY, 'true')
    setHasTransferredSession(true)

    // Clear the flag after a delay to ensure it's not persisted indefinitely
    setTimeout(() => {
      localStorage.removeItem(SESSION_TRANSFER_FLAG_KEY)
      setHasTransferredSession(false)
    }, 60000) // Clear after 1 minute
  }, [])

  // Function to update the last activity timestamp
  const updateLastActivity = useCallback(() => {
    const now = Date.now()
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString())
    localStorage.setItem(SESSION_ACTIVE_KEY, 'true')
    setIsActive(true)
  }, [])

  // Function to check if the current session is still valid
  const validateSession = useCallback(async () => {
    if (!auth.isAuthenticated || !auth.user?.profile?.sub || !auth.user?.access_token || !auth.user?.session_state) {
      return
    }

    try {
      console.log('Validating session...')
      const userId = auth.user?.profile?.sub
      const accessToken = auth.user?.access_token
      const sessionState = auth.user?.session_state

      if (!userId || !accessToken || !sessionState) {
        return
      }

      // Check if this browser/tab is considered active
      // These variables are no longer needed as the related code is commented out
      // const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY)
      // const lastActivity = lastActivityStr ? parseInt(lastActivityStr, 10) : 0
      // const now = Date.now()
      // const timeSinceLastActivity = now - lastActivity

      // Comment out session validity check to prevent API calls
      // This was causing 403 errors in the console
      /*
      // If this is an inactive browser and the session has been terminated, log out
      const { isValid, isTerminated } = await checkSessionValidity(userId, sessionState, accessToken)

      if (!isValid) {
        // If we've just transferred the session (logged out other sessions), don't redirect
        if (hasTransferredSession) {
          console.log('Session transfer in progress, not redirecting to login')
          return
        }

        // If this is an inactive browser (no recent activity) and the session is terminated,
        // we should log out immediately
        if (timeSinceLastActivity > INACTIVITY_THRESHOLD && isTerminated) {
          console.log('Inactive browser with terminated session, logging out')
          localStorage.removeItem(SESSION_ACTIVE_KEY)
          setIsActive(false)
          await auth.signoutRedirect()
          return
        }

        // For active browsers, we still want to check if the session is valid
        // If there are no sessions at all, it might be because we just terminated all other sessions
        // In that case, don't redirect to login
        const sessions = await getUserSessions(userId, accessToken)
        if (sessions.length === 0) {
          console.log('No sessions found, possibly due to recent session transfer. Not redirecting to login.')
          return
        }

        console.log('Session no longer valid, redirecting to login')
        // Session is no longer valid, sign out and redirect to login
        await auth.signoutRedirect()
      } else {
        console.log('Session is still valid')
      }
      */

      // Instead of checking session validity, just log that we're assuming the session is valid
      console.log('Session validation disabled, assuming session is valid')
    } catch (error) {
      console.error('Error validating session:', error)

      // Check if it's a network error from axios
      if (axios.isAxiosError(error) && error.message === 'Network Error') {
        console.error('Network error detected during session validation')
        // We'll let the ProtectedApp component handle this error
        // by propagating it up
        throw error
      }

      // Don't sign out on other errors, as it might be a temporary issue
    }
  }, [auth, hasTransferredSession])

  // Set up activity tracking
  useEffect(() => {
    // Update activity on mount
    updateLastActivity()

    // Set up event listeners for user activity
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll']

    const handleActivity = () => {
      updateLastActivity()
    }

    // Add event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity)
    })

    // Clean up event listeners
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
    }
  }, [updateLastActivity])

  // Set up periodic session validation
  useEffect(() => {
    // Only set up validation if user is authenticated
    if (!auth.isAuthenticated) {
      return
    }

    // Validate immediately on mount or when auth changes
    validateSession()

    // Clear any existing intervals
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
    }

    if (activityTimerRef.current) {
      window.clearInterval(activityTimerRef.current)
    }

    // Set up interval for periodic validation
    timerRef.current = window.setInterval(() => {
      validateSession()
    }, intervalMs)

    // Set up interval for checking activity status
    activityTimerRef.current = window.setInterval(() => {
      const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY)
      const lastActivity = lastActivityStr ? parseInt(lastActivityStr, 10) : 0
      const now = Date.now()
      const timeSinceLastActivity = now - lastActivity

      // If inactive for too long, mark as inactive
      if (timeSinceLastActivity > INACTIVITY_THRESHOLD) {
        localStorage.setItem(SESSION_ACTIVE_KEY, 'false')
        setIsActive(false)
      } else {
        localStorage.setItem(SESSION_ACTIVE_KEY, 'true')
        setIsActive(true)
      }
    }, ACTIVITY_CHECK_INTERVAL)

    // Clean up intervals on unmount or when dependencies change
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current)
        timerRef.current = undefined
      }

      if (activityTimerRef.current) {
        window.clearInterval(activityTimerRef.current)
        activityTimerRef.current = undefined
      }
    }
  }, [auth.isAuthenticated, validateSession, intervalMs, updateLastActivity])

  // Validate session before any API call or important user action
  const validateSessionBeforeAction = useCallback(async () => {
    if (!auth.isAuthenticated || !auth.user?.profile?.sub || !auth.user?.access_token || !auth.user?.session_state) {
      return false
    }

    try {
      const userId = auth.user?.profile?.sub
      const accessToken = auth.user?.access_token
      const sessionState = auth.user?.session_state

      if (!userId || !accessToken || !sessionState) {
        return false
      }

      // Update activity timestamp when performing an action
      updateLastActivity()

      // Comment out session validity check to prevent API calls
      // This was causing 403 errors in the console
      /*
      const { isValid, isTerminated } = await checkSessionValidity(userId, sessionState, accessToken)

      if (!isValid) {
        // If we've just transferred the session, don't redirect
        if (hasTransferredSession) {
          console.log('Session transfer in progress, continuing with action')
          return true
        }

        // Check if there are any sessions for this user
        // If there are no sessions at all, it might be because we just terminated all other sessions
        // In that case, don't redirect to login
        if (!isTerminated) {
          console.log(
            'No sessions found before action, possibly due to recent session transfer. Continuing with action.',
          )
          return true
        }

        console.log('Session no longer valid before action, redirecting to login')
        await auth.signoutRedirect()
        return false
      }
      */

      // Instead of checking session validity, just log that we're assuming the session is valid
      console.log('Session validation before action disabled, assuming session is valid')
      return true
    } catch (error) {
      console.error('Error validating session before action:', error)

      // Check if it's a network error from axios
      if (axios.isAxiosError(error) && error.message === 'Network Error') {
        console.error('Network error detected during session validation before action')
        // Propagate the error up so it can be handled by the ProtectedApp component
        throw error
      }

      // Continue with action on other errors, as they might be temporary issues
      return true
    }
  }, [auth, hasTransferredSession, updateLastActivity])

  return {
    validateSessionBeforeAction,
    markSessionTransferred,
    isActive,
  }
}
